import {
  Component,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DataConnection } from '@nimmsta/ng-commons-lib/models';
import { DataConnectionService } from '@nimmsta/ng-workflow-lib/services/data-connection';
import { TranslatePipe } from '@ngx-translate/core';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { PrimeTemplate } from 'primeng/api';
import { DatePicker } from 'primeng/datepicker';
import dayjs from 'dayjs';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  DailyScanTimesDashboardConfiguration,
  MAX_DATE_RANGE_DAYS,
  MAX_FILTERS,
} from './daily-scan-times-dashboard-configuration';
import {
  FilterOperator,
  ResultFieldFilter,
} from '../job-result-analytics-dashboard/job-result-analytics-dashboard-configuration';
import {
  JobResultAnalyticsDashboardService,
  FieldInfo,
  JobResultFieldsResponse,
} from '../job-result-analytics-dashboard/job-result-analytics-dashboard.service';
import { JobDashboardRange } from '../job-dashboard/job-dashboard-configuration';
import {
  DASHBOARD_EDITOR_CONTEXT,
  DashboardEditorContext,
  filterAvailableDataConnectionIds,
  getDeclaredResultFields,
  isWorkflowTemplateDashboard,
} from '../../../../models/dashboard';

/** Matches date-only, local date-time and offset date-time values as accepted by the backend. */
const TIMESTAMP_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}([T ].+)?$/;

@Component({
  selector: 'app-daily-scan-times-dashboard-form',
  templateUrl: './daily-scan-times-dashboard-form.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MultiSelect,
    Select,
    PrimeTemplate,
    DatePicker,
  ],
})
export class DailyScanTimesDashboardFormComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data = inject<DailyScanTimesDashboardConfiguration>('formData' as any);
  private fb = inject(FormBuilder);
  private dataConnectionService = inject(DataConnectionService);
  private analyticsService = inject(JobResultAnalyticsDashboardService);
  private editorContext = inject<DashboardEditorContext | null>(
    DASHBOARD_EDITOR_CONTEXT,
    { optional: true },
  );
  private readonly destroy$ = new Subject<void>();
  private readonly discoverFieldsTrigger$ = new Subject<{
    dataConnectionIds: string[];
    forceRefresh: boolean;
  }>();

  form!: FormGroup;

  @Output() dataChange =
    new EventEmitter<DailyScanTimesDashboardConfiguration>();

  dataConnections: DataConnection[] = [];
  availableFields: FieldInfo[] = [];
  isLoadingFields = false;
  isTemplateDashboard = isWorkflowTemplateDashboard(this.editorContext);

  customRange = JobDashboardRange.CUSTOM;
  dateRange: Date[] = [];
  dateRangeError = '';

  // Filter out ranges that could exceed MAX_DATE_RANGE_DAYS (91 days)
  private readonly excludedRanges = new Set([
    JobDashboardRange.LAST_365_DAYS,
    JobDashboardRange.ALL_TIME,
    JobDashboardRange.THIS_QUARTER,
    JobDashboardRange.THIS_YEAR,
  ]);

  rangeOptions = Object.entries(JobDashboardRange)
    .filter(([, value]) => !this.excludedRanges.has(value as JobDashboardRange))
    .map(([key, value]) => ({
      key,
      value: value as JobDashboardRange,
    }));

  sortOrderOptions = ['desc', 'asc'];

  maxFilters = MAX_FILTERS;
  maxDateRangeDays = MAX_DATE_RANGE_DAYS;

  filterOperators = Object.entries(FilterOperator).map(([key, value]) => ({
    key,
    value: value as FilterOperator,
  }));

  statusOptions = [
    { key: 'OPEN', value: 'OPEN' },
    { key: 'IN_PROGRESS', value: 'IN_PROGRESS' },
    { key: 'COMPLETE', value: 'COMPLETE' },
    { key: 'CANCELLED', value: 'CANCELLED' },
  ];

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      title: [data?.title || ''],
      description: [data?.description || ''],
      dataConnectionIds: [data?.dataConnectionIds || []],
      statuses: [data?.statuses || []],
      range: [data?.range || JobDashboardRange.TODAY],
      sortOrder: [data?.sortOrder ?? 'desc'],
      from: [data?.from || null],
      to: [data?.to || null],
      scanTimestampFieldKey: [
        data?.scanTimestampFieldKey || null,
        Validators.required,
      ],
      filters: this.fb.array(
        data?.filters?.map((f) => this.createFilterGroup(f)) || [],
      ),
    });

    if (data?.from && data?.to) {
      this.dateRange = [dayjs(data.from).toDate(), dayjs(data.to).toDate()];
    }

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.emitConfiguration();
    });

    this.emitConfiguration();
  }

  ngOnInit() {
    if (this.isTemplateDashboard) {
      this.availableFields = getDeclaredResultFields(this.editorContext);
      this.form.patchValue({ dataConnectionIds: [] });
      return;
    }
    this.loadDataConnections();

    this.discoverFieldsTrigger$
      .pipe(
        tap(() => {
          this.isLoadingFields = true;
        }),
        switchMap(({ dataConnectionIds, forceRefresh }) =>
          this.analyticsService
            .discoverFields(dataConnectionIds, forceRefresh)
            .pipe(
              catchError(() =>
                of({ fields: [], sampleSize: 0 } as JobResultFieldsResponse),
              ),
            ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.availableFields = response.fields;
        this.isLoadingFields = false;
      });

    this.form
      .get('dataConnectionIds')
      ?.valueChanges.pipe(
        startWith(this.form.get('dataConnectionIds')?.value),
        map((value) => this.normalizeDataConnectionIds(value)),
        debounceTime(250),
        distinctUntilChanged((prev, curr) => this.haveSameEntries(prev, curr)),
        takeUntil(this.destroy$),
      )
      .subscribe((dataConnectionIds) => {
        this.requestFieldDiscovery(dataConnectionIds, false);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filters(): FormArray {
    return this.form.get('filters') as FormArray;
  }

  addFilter() {
    this.filters.push(this.createFilterGroup());
  }

  removeFilter(index: number) {
    this.filters.removeAt(index);
  }

  discoverFields() {
    if (this.isTemplateDashboard) {
      return;
    }
    const dataConnectionIds = this.normalizeDataConnectionIds(
      this.form?.get('dataConnectionIds')?.value,
    );
    this.requestFieldDiscovery(dataConnectionIds, true);
  }

  /**
   * Field options for the scan timestamp select. Fields whose sample values look like
   * timestamps are preferred; if none match, all fields are offered as a fallback.
   */
  getTimestampFieldOptions() {
    const timestampFields = this.availableFields.filter((field) =>
      this.isLikelyTimestampField(field),
    );
    const fields =
      timestampFields.length > 0 ? timestampFields : this.availableFields;
    return fields.map((f) => ({
      key: f.key,
      value: f.key,
      label: `${f.key} (${f.inferredType})`,
    }));
  }

  getFieldOptions() {
    return this.availableFields.map((f) => ({
      key: f.key,
      value: f.key,
      label: `${f.key} (${f.inferredType})`,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customRangeChange(event: any) {
    const from = event[0] ? dayjs(event[0]) : null;
    const to = event[1] ? dayjs(event[1]) : null;

    // Validate date range doesn't exceed maximum
    if (from && to) {
      const daysDiff = to.diff(from, 'day');
      if (daysDiff > MAX_DATE_RANGE_DAYS) {
        this.dateRangeError = `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days (selected: ${daysDiff} days)`;
        return;
      }
    }
    this.dateRangeError = '';

    this.form.patchValue({
      range: this.customRange,
      from,
      to,
    });
  }

  private createFilterGroup(filter?: ResultFieldFilter) {
    return this.fb.group({
      fieldKey: [filter?.fieldKey || '', Validators.required],
      operator: [
        filter?.operator || FilterOperator.EQUALS,
        Validators.required,
      ],
      value: [filter?.value || '', Validators.required],
    });
  }

  private isLikelyTimestampField(field: FieldInfo): boolean {
    if (field.inferredType === 'timestamp') {
      return true;
    }
    return field.sampleValues.some((value) =>
      TIMESTAMP_VALUE_REGEX.test(value),
    );
  }

  private loadDataConnections() {
    this.dataConnectionService
      .simpleList()
      .subscribe((connections: DataConnection[]) => {
        connections.sort((a, b) => a.name.localeCompare(b.name));
        this.dataConnections = connections;
        this.removeUnavailableDataConnectionIds(connections);
      });
  }

  private requestFieldDiscovery(
    dataConnectionIds: string[],
    forceRefresh: boolean,
  ) {
    this.discoverFieldsTrigger$.next({ dataConnectionIds, forceRefresh });
  }

  private normalizeDataConnectionIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return [...new Set(value)]
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .sort();
  }

  private haveSameEntries(a: string[], b: string[]): boolean {
    return (
      a.length === b.length && a.every((value, index) => value === b[index])
    );
  }

  private removeUnavailableDataConnectionIds(connections: DataConnection[]) {
    this.form.patchValue({
      dataConnectionIds: filterAvailableDataConnectionIds(
        this.form.get('dataConnectionIds')?.value,
        connections.map((connection) => connection.id),
      ),
    });
  }

  private emitConfiguration() {
    const value = this.form.getRawValue();

    const config: DailyScanTimesDashboardConfiguration = {
      title: value.title,
      description: value.description,
      dataConnectionIds: value.dataConnectionIds,
      statuses: value.statuses,
      range: value.range,
      sortOrder: value.sortOrder,
      from: value.from,
      to: value.to,
      scanTimestampFieldKey: value.scanTimestampFieldKey || undefined,
      filters: value.filters,
    };

    this.dataChange.emit(config);
  }
}
